import "./gtakTable.scss";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Delete, Add } from "@mui/icons-material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useSelector } from "react-redux";

const GtakTable = () => {
  const userId = useSelector((state) => state.user.userId);
  const adminType = useSelector((state) => state.user.adminType);
  const email = useSelector((state) => state.user.userEmail);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loadCount, setLoadCount] = useState(5); // Keeping your existing state
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const finalUserId = adminType === "sAdmin" ? "1" : userId;

  // ✅ FETCH VIDEOS (FIXED WITH HEIGHT & SYNC LOGIC)
  useEffect(() => {
    const fetchVideos = async () => {
      // Logic: Only skip if email is missing or we've reached the end on a page > 1
      if (!email) return;

      setLoading(true);

      console.log(`Fetching videos for email: ${email}, page: ${page}`);

      try {
        const res = await axios.get(
          `${BASE_URL}/api/userVideoFile/findAllUserVideos`,
          {
            params: {
              emailRef: email,
              page,
              limit: 5,
            },
          },
        );

        const newVideos = res.data.videos || [];

        // If we get less than 5, there are no more records to load
        if (newVideos.length < 5) {
          setHasMore(false);
        }

        setVideos((prev) => {
          // If page 1, we start fresh. If page > 1, we append.
          const merged = page === 1 ? newVideos : [...prev, ...newVideos];

          // Your existing Map logic to ensure unique IDs
          const unique = Array.from(
            new Map(merged.map((v) => [v._id, v])).values(),
          );

          return unique;
        });
      } catch (err) {
        console.error("Fetch Error:", err);
        // Don't clear videos on error so existing list stays visible
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [email, page, BASE_URL]);

  // ✅ DELETE VIDEO
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/userVideoFile/deleteVideoFile/${id}`);
      setVideos((prev) => prev.filter((item) => item._id !== id));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handlePlay = (videoData) => {
    navigate("/gtak/playGtak", { state: { video: videoData } });
  };

  const columns = [
    { field: "_id", headerName: "ID", width: 70 },
    {
      field: "thumbnailUrl",
      headerName: "Image",
      width: 100,
      renderCell: (params) => (
        <img className="cellImg" src={params.row.thumbnailUrl} alt="" />
      ),
    },
    { field: "videoTitle", headerName: "Title", width: 200 },
    { field: "videoCategory", headerName: "Category", width: 120 },
    { field: "videoDescription", headerName: "Description", width: 250 },
    {
      field: "action",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <div className="cellAction">
          <IconButton onClick={() => handlePlay(params.row)}>
            <PlayCircleIcon />
          </IconButton>

          <IconButton
            onClick={() => {
              setSelectedVideo(params.row);
              setOpenDialog(true);
            }}
            color="error"
          >
            <Delete />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <Typography variant="h5">List Of Videos</Typography>

        <Link to="/gtak/newGtak">
          <Button variant="contained">Add Video</Button>
        </Link>
      </div>

      <Grid container justifyContent="center">
        {/* 🔥 FIX: The DataGrid MUST be in a div with a fixed height */}
        <div style={{ height: 500, width: "100%", marginTop: "10px" }}>
          {loading && videos.length === 0 ? (
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ height: "100%" }}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <DataGrid
              rows={videos}
              columns={columns}
              getRowId={(row) => row._id}
              hideFooterPagination
              disableSelectionOnClick
            />
          )}
        </div>
      </Grid>

      {/* 🔥 LOAD MORE BUTTON */}
      {!loading && hasMore && (
        <Grid container justifyContent="center" style={{ marginTop: 20 }}>
          <Button
            variant="contained"
            onClick={() => setPage((p) => p + 1)}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More"}
          </Button>
        </Grid>
      )}

      {/* NO MORE VIDEOS LABEL */}
      {!hasMore && videos.length > 0 && (
        <Typography align="center" sx={{ mt: 2, color: "gray" }}>
          No more videos to load
        </Typography>
      )}

      {/* DELETE DIALOG */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Delete Video?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this video?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              handleDelete(selectedVideo._id);
              setOpenDialog(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GtakTable;
